import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Clock,
  MessageSquare,
  CreditCard,
  LogOut,
  Plus,
  Trash2,
  Check,
  Star,
  Shield,
} from "lucide-react";

const ClientPaymentMethods = () => {
  const { user, token, logout, API } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(`${API}/client/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPaymentMethods(data.payment_methods || []);
      setDefaultMethod(data.default_payment_method);
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    setAdding(true);
    try {
      const response = await fetch(`${API}/client/payment-methods/setup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to initiate setup");
      
      const data = await response.json();
      
      // Load Stripe.js and redirect to card setup
      // For simplicity, we'll use Stripe Checkout's card setup
      // In production, you'd use Stripe Elements for inline card input
      toast.info("Card setup feature requires Stripe Elements integration. Please pay an invoice to save your card automatically.");
      
    } catch (error) {
      toast.error("Failed to add card");
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      const response = await fetch(`${API}/client/payment-methods/${paymentMethodId}/default`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to set default");
      
      toast.success("Default payment method updated");
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to update default card");
    }
  };

  const handleDelete = async (paymentMethodId) => {
    if (!confirm("Are you sure you want to remove this card?")) return;
    
    try {
      const response = await fetch(`${API}/client/payment-methods/${paymentMethodId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error("Failed to delete");
      
      toast.success("Card removed");
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to remove card");
    }
  };

  const getCardBrandIcon = (brand) => {
    // Simple text representation - in production, use card brand SVGs
    const brands = {
      visa: "VISA",
      mastercard: "MC",
      amex: "AMEX",
      discover: "DISC",
    };
    return brands[brand?.toLowerCase()] || brand?.toUpperCase() || "CARD";
  };

  const navItems = [
    { name: "Dashboard", path: "/portal", icon: LayoutDashboard },
    { name: "Invoices", path: "/portal/invoices", icon: FileText },
    { name: "Payment Methods", path: "/portal/payment-methods", icon: CreditCard, active: true },
    { name: "Chat", path: "/portal/chat", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-rnb-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">R</span>
            </div>
            <span className="font-heading font-semibold text-lg text-rnb-navy">
              Client Portal
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <span className="text-sm text-rnb-gray">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-73px)] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  item.active
                    ? "bg-rnb-light text-rnb-blue"
                    : "text-rnb-gray hover:bg-slate-50"
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-3xl font-bold text-rnb-navy mb-2">
                Payment Methods
              </h1>
              <p className="text-rnb-gray">
                Manage your saved payment methods for faster checkout
              </p>
            </div>

            {/* Security Notice */}
            <Card className="mb-6 border-rnb-teal/30 bg-rnb-teal/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-rnb-navy text-sm">Secure Payment Processing</h3>
                    <p className="text-xs text-rnb-gray mt-1">
                      Your payment information is securely stored and processed by Stripe. We never store your full card details.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods List */}
            <Card data-testid="payment-methods-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-rnb-blue" />
                      Saved Cards
                    </CardTitle>
                    <CardDescription>
                      Cards saved from previous payments
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <div className="space-y-4">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          method.is_default ? "border-rnb-blue bg-rnb-light/50" : "border-slate-200 bg-slate-50"
                        }`}
                        data-testid={`payment-method-${method.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-10 bg-gradient-to-r from-slate-700 to-slate-900 rounded-md flex items-center justify-center text-white text-xs font-bold">
                            {getCardBrandIcon(method.brand)}
                          </div>
                          <div>
                            <p className="font-medium text-rnb-navy">
                              •••• •••• •••• {method.last4}
                            </p>
                            <p className="text-sm text-rnb-gray">
                              Expires {method.exp_month}/{method.exp_year}
                            </p>
                          </div>
                          {method.is_default && (
                            <Badge className="bg-rnb-blue">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!method.is_default && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetDefault(method.id)}
                              data-testid={`set-default-${method.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Set Default
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(method.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-${method.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                    <h3 className="font-medium text-rnb-navy mb-2">No saved cards</h3>
                    <p className="text-sm text-rnb-gray mb-6">
                      Your cards will be saved automatically when you make a payment.
                    </p>
                    <Link to="/portal/invoices">
                      <Button>
                        View Invoices
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-medium text-rnb-navy mb-2">How it works</h3>
                <ul className="space-y-2 text-sm text-rnb-gray">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-rnb-teal flex-shrink-0 mt-0.5" />
                    When you pay an invoice, your card can be saved for future use
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-rnb-teal flex-shrink-0 mt-0.5" />
                    Set a default card to speed up checkout
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-rnb-teal flex-shrink-0 mt-0.5" />
                    Remove cards anytime - we&apos;ll never charge without your consent
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientPaymentMethods;
